/**
 * Created by rathompson on 1/21/22
 * File: IAMReducer
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { ARRAY, OBJECT } from '../utils/Utils'
import { difference, omit, cloneDeep, keyBy, groupBy } from 'lodash'
import buildPermissionsTree from '../../iam/utils/buildPermissionTree'

import {
  SET_PERMISSIONS,
  SET_ROLES,
  SET_ACCESS_POLICIES,
  SET_SERVICE_ACCOUNTS,
  SET_PERMISSION_SETS,
  SET_CUSTOM_ROLES,
  SET_CUSTOM_ROLE_METADATA,
  SET_ALLOWED_IPS,
  SET_FEDERATION_MAPPINGS,
  IAM_RESET,
  ActionSetRoles,
  ActionSetPermissions,
  ActionSetAccessPolicies,
  ActionSetCustomRoleMetaData,
  ActionSetCustomRoles,
  ActionSetPermissionSets,
  ActionSetServiceAccounts,
  ActionSetFederationMappings,
  ActionSetAllowedIps
} from './actions/IAMReducer.actions'

import type { Action } from './actions'

import type {
  IAMReducerState,
  IAM_APIRole,
  IAM_APIPermission,
  IAM_APIAccessPolicy,
  IAM_Role,
  IAM_Permission, IAM_PermissionSet, IAM_FederationMapping
} from './types/IAMReducer.types'
import { getVarValue } from '../utils/Sparky'
import { FF_IAM_USE_ONEAPP_NAME } from '../utils/FeatureFlags'

const useOneAppName = getVarValue(FF_IAM_USE_ONEAPP_NAME)

export const IAM_INITIAL_STATE: IAMReducerState = OBJECT.deepFreeze({
  roles: [],
  roleMap: {},
  permissions: [],
  permissionsMap: {},
  permissionsTree: [],
  accessPolicies: [],
  policiesMap: {},
  serviceAccounts: [],
  permissionSets: [],
  setMap: {},
  customRoles: [],
  customRoleMap: {},
  customRoleFormMetaData: {},
  federationMappings: [],
  federationMappingsMap: {},
  allowedIps: [],
  allowedIpMap: {}
})

const buildPermissionsMap: (permissions: IAM_APIPermission[]) => Record<string, IAM_Permission> = (permissions = []) => {
  return cloneDeep(keyBy(permissions, 'name'))
}

const buildRoleMap: (roles: IAM_APIRole[]) => Record<string, IAM_Role> = (roles = []) => roles.reduce((map, role) => {
  map[role.name] = cloneDeep({
    ...role,
    id: role.name,
    is_custom: false,
  })
  return map
}, {} as Record<string, IAM_Role>)

const buildFederationMappingsMap: (federationMappings: IAM_FederationMapping[]) => Record<string, Array<IAM_FederationMapping>> = (federationMappings = []) => {
  return cloneDeep(groupBy(federationMappings, 'domain'))
}

const setRoles: (state: IAMReducerState, action: ActionSetRoles) => IAMReducerState = (state, action) => {
  const roleMap: { [key: string]: IAM_Role } = buildRoleMap(action.roles)
  const data = Object.values(roleMap).map(role => cloneDeep({
    ...role,
    explicit_permissions: ARRAY.clean(role.permissions),
    read_only_permission_sets: ARRAY.clean(role.permission_sets?.filter(set => set?.access_types?.includes('read') && !set?.access_types?.includes('write'))),
    read_write_permission_sets: ARRAY.clean(role.permission_sets?.filter(set => set?.access_types?.includes('write'))),
  }))
  return { ...state, roles: data, roleMap: keyBy(data, 'id') }
}

const setPermissions: (state: IAMReducerState, action: ActionSetPermissions) => IAMReducerState = (state, { permissions }) => ({
  ...state,
  permissions: permissions,
  permissionsMap: buildPermissionsMap(permissions),
  permissionsTree: buildPermissionsTree(permissions)
})

const setCustomRoles: (state: IAMReducerState, action: ActionSetCustomRoles) => IAMReducerState = (state: IAMReducerState, { customRoles }) => {
  const data = cloneDeep(customRoles.map(role => ({
    ...role,
    explicit_permissions: ARRAY.clean(role.permissions),
    read_only_permission_sets: ARRAY.clean(role.permission_sets?.filter(set => set?.access_types?.includes('read') && !set?.access_types?.includes('write'))),
    read_write_permission_sets: ARRAY.clean(role.permission_sets?.filter(set => set?.access_types?.includes('write'))),
    is_custom: true,
  })))
  const customRoleMap = cloneDeep(data.reduce((map, role) => {
    map[role.id] = role
    return map
  }, {} as Record<string, IAM_Role>))
  return { ...state, customRoles: data, customRoleMap }
}

const setPermissionSets: (state: IAMReducerState, action: ActionSetPermissionSets) => IAMReducerState = (state, { permissionSets }) => {
  const data = cloneDeep(permissionSets.map(set => ({
    ...set,
    display_name: (useOneAppName && ['Prisma Access', 'Strata Access'].includes(set.display_name)) ? `${set.display_name} + NGFW` : set.display_name,
    id: set.name,
    children: permissionSets.filter(item => item.parent === set.name).map(({ name }) => name),
    web_ui_permissions: difference(set.aggregated_permissions, set.permissions),
    api_permissions: set.permissions
  })))
  const setMap = cloneDeep(data.reduce((map, set: IAM_PermissionSet) => {
    map[set.id] = set
    return map
  }, {} as Record<string, IAM_PermissionSet>))
  return { ...state, permissionSets: data, setMap }
}

const setAccessPolicies: (state: IAMReducerState, action: ActionSetAccessPolicies) => IAMReducerState = (state, { accessPolicies }) => {
  const items = ARRAY.clean(accessPolicies)
  const policiesMap = items.reduce((map: { [key: string]: IAM_APIAccessPolicy[] }, policy: IAM_APIAccessPolicy) => {
    if (!map.hasOwnProperty(policy.principal)) {
      map[policy.principal] = []
    }
    map[policy.principal].push(policy)
    return map
  }, {})
  return { ...state, accessPolicies: items, policiesMap }
}

const setServiceAccount: (state: IAMReducerState, action: ActionSetServiceAccounts) => IAMReducerState = (state, { serviceAccounts }) => ({
  ...state,
  serviceAccounts
})

const setCustomRoleMetadata: (state: IAMReducerState, action: ActionSetCustomRoleMetaData) => IAMReducerState = (state, { metadata }) => ({
  ...state,
  customRoleFormMetaData: metadata
})

const setFederationMappings: (state: IAMReducerState, action: ActionSetFederationMappings) => IAMReducerState = (state, { federationMappings }) => ({
  ...state,
  federationMappings,
  federationMappingsMap: buildFederationMappingsMap(federationMappings)
})

const setAllowedIps: (state: IAMReducerState, action: ActionSetAllowedIps) => IAMReducerState = (state, { allowedIps }) => ({
  ...state,
  allowedIps
})

const IAMReducer: (state: IAMReducerState, action: Action) => IAMReducerState = (state = IAM_INITIAL_STATE, action) => {
  switch (action.type) {
    case SET_ROLES:
      return setRoles(state, action as ActionSetRoles)
    case SET_PERMISSIONS:
      return setPermissions(state, action as ActionSetPermissions)
    case SET_ACCESS_POLICIES:
      return setAccessPolicies(state, action as ActionSetAccessPolicies)
    case SET_SERVICE_ACCOUNTS:
      return setServiceAccount(state, action as ActionSetServiceAccounts)
    case SET_CUSTOM_ROLES:
      return setCustomRoles(state, action as ActionSetCustomRoles)
    case SET_PERMISSION_SETS:
      return setPermissionSets(state, action as ActionSetPermissionSets)
    case SET_CUSTOM_ROLE_METADATA:
      return setCustomRoleMetadata(state, action as ActionSetCustomRoleMetaData)
    case SET_FEDERATION_MAPPINGS:
      return setFederationMappings(state, action as ActionSetFederationMappings)
    case SET_ALLOWED_IPS:
      return setAllowedIps(state, action as ActionSetAllowedIps)
    case IAM_RESET:
      return {
        ...state, ...omit(IAM_INITIAL_STATE, [
          'roles',
          'roleMap',
          'permissions',
          'permissionsMap',
          'permissionsTree',
          'permissionSets',
          'setMap',
          'customRoleFormMetaData'
        ])
      }
    default:
      return state
  }
}

(IAMReducer as unknown as { initialState: IAMReducerState }).initialState = IAM_INITIAL_STATE
export default IAMReducer
